import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Icon,
  SimpleGrid,
  Divider,
  List,
  ListItem,
  ListIcon,
  Grid,
  GridItem,
  Flex,
  Center,
  Badge,
} from '@chakra-ui/react';
import HeaderMenus from './HeaderMenus';
import {
  FiFileText,
  FiCpu,
  FiCheckCircle,
  FiXCircle,
  FiArrowDown,
  FiArrowRight,
  FiThumbsUp,
  FiShield,
  FiBookOpen,
  FiClipboard,
} from 'react-icons/fi';

// A reusable card for highlighting features, benefits, or problems
const FeatureCard = ({ icon, title, description, colorScheme }) => (
  <Card shadow="lg" textAlign="center" borderTop="4px" borderTopColor={`${colorScheme}.400`} h="full">
    <CardBody py={8}>
      <VStack spacing={4}>
        <Center w={20} h={20} bg={`${colorScheme}.100`} borderRadius="full">
          <Icon as={icon} boxSize={10} color={`${colorScheme}.600`} />
        </Center>
        <Heading size="md" color="gray.800">{title}</Heading>
        <Text color="gray.600" px={4}>{description}</Text>
      </VStack>
    </CardBody>
  </Card>
);

const CPTCodingWorkflow = () => {
  return (
    <Grid
      templateAreas={`"header header" "main main"`}
      gridTemplateRows={'60px 1fr'}
      gridTemplateColumns={'1fr'}
      h='100vh'
      gap='1'
    >
      <HeaderMenus />
      <GridItem area={'main'} bg="gray.50" overflowY="auto">
        <Container maxW="7xl" py={16}>
          <VStack spacing={16} align="stretch">
            <VStack spacing={6} textAlign="center">
              <Badge colorScheme="purple" fontSize="md" px={4} py={2} borderRadius="full">
                Engine Deep Dive
              </Badge>
              <Heading size="2xl" color="gray.800">Beyond Automation: The Revedy CPT Coding Engine</Heading>
              <Text fontSize="xl" color="gray.600" maxW="4xl">
                From complex clinical notes to precise, compliant, and defensible CPT codes in seconds.
              </Text>
            </VStack>

            {/* Section 1: Before and After */}
            <VStack spacing={4}>
                <Heading size="xl" color="gray.700">Manual Coding Challenges</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Coder Variability" description="Inconsistent coding based on individual interpretation and experience." />
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Missed Revenue" description="Uncaptured procedures and incorrect units lead to lost revenue." />
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Compliance Risk" description="Struggling to keep up with complex NCCI edits and payer-specific rules." />
                  <FeatureCard icon={FiXCircle} colorScheme="red" title="Slow Turnaround" description="Manual review and coding creates bottlenecks and delays billing." />
                </SimpleGrid>
            </VStack>

            <Flex justify="center" align="center" direction="column">
                <Icon as={FiArrowDown} boxSize={16} color="gray.400" />
                <Text fontWeight="bold" color="purple.600" mt={2}>IS TRANSFORMED BY</Text>
            </Flex>

            <VStack spacing={4}>
                <Heading size="xl" color="gray.700">The Revedy Coding Advantage</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Unmatched Precision" description="Our AI understands clinical context to assign the most accurate codes." />
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Complete Capture" description="Ensures all billable procedures are identified and correctly coded." />
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Built-in Compliance" description="Automatically validates codes against a massive library of rules." />
                  <FeatureCard icon={FiCheckCircle} colorScheme="green" title="Instantaneous Speed" description="Analyzes and codes entire cases in seconds, not hours." />
                </SimpleGrid>
            </VStack>

            <Divider my={8} />

            {/* Section 2: The Revedy Workflow */}
            <VStack spacing={8} align="stretch">
              <Heading size="xl" textAlign="center" color="gray.700">How It Works: From Raw Document to Rich Insights</Heading>
              
              {/* Part A: Inputs */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="purple.600" textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={6}>
                  Part A: We Synthesize the Complete Clinical Story
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                  <Card shadow="xl" h="full" borderTop="4px" borderColor="blue.400">
                    <CardBody p={6}>
                      <VStack spacing={4} textAlign="center">
                        <Icon as={FiFileText} boxSize={12} color="blue.500" />
                        <Heading size="md">1. Your Clinical Documentation</Heading>
                        <Text fontSize="md">The engine ingests all case files, including operative notes, reports, and transcripts.</Text>
                        <Badge colorScheme="blue">Source: Case Context</Badge>
                      </VStack>
                    </CardBody>
                  </Card>
                  <Card shadow="xl" h="full" borderTop="4px" borderColor="orange.400">
                    <CardBody p={6}>
                      <VStack spacing={4} textAlign="center">
                        <Icon as={FiBookOpen} boxSize={12} color="orange.500" />
                        <Heading size="md">2. Official Code Libraries</Heading>
                        <Text fontSize="md">We cross-reference against official AMA (CPT®) and CMS (HCPCS, RVU) databases.</Text>
                        <Badge colorScheme="orange">Source: Foundational Rules</Badge>
                      </VStack>
                    </CardBody>
                  </Card>
                  <Card shadow="xl" h="full" borderTop="4px" borderColor="teal.400">
                    <CardBody p={6}>
                      <VStack spacing={4} textAlign="center">
                        <Icon as={FiShield} boxSize={12} color="teal.500" />
                        <Heading size="md">3. Payer & Client Rules</Heading>
                        <Text fontSize="md">We apply our vast library of payer policies and your own organization's specific guidelines.</Text>
                        <Badge colorScheme="teal">Source: Custom Logic</Badge>
                      </VStack>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              </Box>

              <Flex justify="center" my={4}>
                <Icon as={FiArrowRight} boxSize={16} color="gray.300" transform={{base: "rotate(90deg)", md: "none"}} />
              </Flex>

              {/* Part B: AI Engine */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="purple.600" textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={6}>
                  Part B: The Revedy AI Engine Reasons Like an Expert
                </Text>
                <Card bgGradient="linear(to-r, gray.100, purple.50)" shadow="2xl" borderWidth="1px" borderColor="gray.200">
                  <CardBody p={8}>
                    <HStack spacing={8} flexDir={{base: "column", md: "row"}}>
                      <Box p={6} borderRadius="2xl" bgGradient="linear(to-br, purple.400, purple.600)" color="white" shadow="lg">
                        <Icon as={FiCpu} boxSize={16} />
                      </Box>
                      <VStack align="start" spacing={4}>
                        <Text fontSize="md"><Text as="span" fontWeight="bold">1. Deconstruct & Classify:</Text> The AI first reads all documents to determine the case type (e.g., IONM, E/M, Gastroenterology) to select the right analytical model.</Text>
                        <Text fontSize="md"><Text as="span" fontWeight="bold">2. Targeted Analysis:</Text> It performs a specialized analysis based on the case type, using either methodical rule-based checks for complex procedures or an autonomous agent for general cases.</Text>
                        <Box p={4} bg="green.50" borderRadius="lg" borderWidth="1px" borderColor="green.200">
                            <Heading size="sm" color="green.700">3. AI-POWERED AUDIT (THE KEY STEP):</Heading>
                            <Text fontSize="md" fontWeight="medium" color="green.800">The engine acts as its own expert auditor, generating a **Confidence Score** and flagging any codes that require human review. It automatically checks for NCCI conflicts and MUE limits.</Text>
                        </Box>
                      </VStack>
                    </HStack>
                  </CardBody>
                </Card>
              </Box>

              <Flex justify="center" my={4}>
                <Icon as={FiArrowRight} boxSize={16} color="gray.300" transform={{base: "rotate(90deg)", md: "none"}} />
              </Flex>

              {/* Part C: Output */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="purple.600" textTransform="uppercase" letterSpacing="wide" textAlign="center" mb={6}>
                  Part C: The Output is More Than Just a Code
                </Text>
                <Card bg="white" shadow="2xl" borderWidth="1px" borderColor="gray.200" borderTop="8px" borderTopColor="purple.400">
                  <CardHeader>
                    <HStack>
                      <Icon as={FiClipboard} boxSize={10} color="purple.600" />
                      <Heading size="lg" color="purple.800">Comprehensive Coding Results</Heading>
                    </HStack>
                  </CardHeader>
                  <CardBody px={8} pb={8}>
                    <List spacing={5}>
                      <ListItem fontSize="lg">
                        <ListIcon as={FiCheckCircle} color='green.500' />
                        <Text as="span" fontWeight="bold">Precise CPT & HCPCS Codes:</Text> Includes all appropriate units and modifiers.
                      </ListItem>
                      <ListItem fontSize="lg">
                        <ListIcon as={FiCheckCircle} color='green.500' />
                        <Text as="span" fontWeight="bold">Actionable Coder Notes:</Text> Provides a clear, line-by-line justification for every single code.
                      </ListItem>
                      <ListItem fontSize="lg">
                        <ListIcon as={FiCheckCircle} color='green.500' />
                        <Text as="span" fontWeight="bold">Confidence Score & Review Guidance:</Text> Delivers an AI-generated confidence level and specific instructions for human reviewers.
                      </ListItem>
                       <ListItem fontSize="lg">
                        <ListIcon as={FiCheckCircle} color='green.500' />
                        <Text as="span" fontWeight="bold">Complete Audit Trail:</Text> Offers a full, transparent record of the AI's reasoning process and the data it used.
                      </ListItem>
                    </List>
                  </CardBody>
                </Card>
              </Box>
            </VStack>

            <Divider my={8} />

            {/* Section 3: The Result */}
            <VStack spacing={8}>
                <Heading size="xl" color="gray.700">The Result: A Smarter, Faster, More Profitable Coding Process</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Maximize Accuracy" description="Reduce errors and coder variability with AI-driven precision." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Ensure Compliance" description="Stay ahead of complex rules with automated validation." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Increase Efficiency" description="Free up your expert coders to focus on high-value tasks." />
                    <FeatureCard icon={FiThumbsUp} colorScheme="green" title="Unlock Insights" description="Leverage rich data, including RVUs, for financial analysis." />
                </SimpleGrid>
            </VStack>

          </VStack>
        </Container>
      </GridItem>
    </Grid>
  );
};

export default CPTCodingWorkflow;